
import type { StateCreator } from 'zustand';
import type { AppState } from '@/lib/store/types';
import type { SubscriptionState, ReferralState } from '@/types';
import { normalizeFeatureFlags, normalizeReferrals, normalizeSubscription } from '../normalizers';
import { getFeatureFlagFallback } from '@/lib/feature-flags';

type SubscriptionSlice = Pick<
    AppState,
    'subscription' | 'featureFlags' | 'referrals' | 'setSubscription' | 'setFeatureFlags' | 'setReferrals' | 'checkFeatureAccess'
>;

const initialSubscriptionState: SubscriptionState = {
    status: 'none',
    tier: 'free',
    currentPeriodEnd: null,
    isPro: false,
    proSource: null,
};

const initialReferralState: ReferralState = {
    code: null,
    referrals: [],
    totalReferrals: 0,
    monthsEarned: 0,
};

export const createSubscriptionSlice: StateCreator<AppState, [], [], SubscriptionSlice> = (set, get) => ({
    // STATE
    subscription: { ...initialSubscriptionState },
    featureFlags: {},
    referrals: { ...initialReferralState },

    // ACTIONS
    setSubscription: (sub) => set((state) => ({
        subscription: normalizeSubscription({ ...state.subscription, ...sub })
    })),

    setFeatureFlags: (flags) => set({ featureFlags: normalizeFeatureFlags(flags) }),

    setReferrals: (ref) => set((state) => ({
        referrals: normalizeReferrals({ ...state.referrals, ...ref })
    })),

    checkFeatureAccess: (key) => {
        const state = get();
        const { userRole, isImpersonating, activeProfileId, profiles } = state;

        // 1. Determine Effective Role
        // If impersonating, we MUST use the role of the profile we are impersonating.
        // If not impersonating, we use the logged-in user's role.
        let effectiveRole = userRole;
        if (isImpersonating && activeProfileId) {
            const targetProfile = profiles.find(p => p.id === activeProfileId);
            // Default to 'athlete' if profile has no specific role assigned
            effectiveRole = targetProfile?.role || 'athlete';
        }

        const featureFlags = normalizeFeatureFlags(state.featureFlags);
        const flag = featureFlags[key] ?? getFeatureFlagFallback(key);
        const isAdmin = effectiveRole === 'admin' || effectiveRole === 'superadmin';

        // 2. Check accessLevel (3-state override) before any other logic
        if (flag?.accessLevel === 'disabled') return false;
        if (flag?.accessLevel === 'admin_only') return isAdmin;
        if (flag?.accessLevel === 'enabled') return true;

        // 3. Admin Bypass (when no restrictive accessLevel is set)
        // Admins (when NOT impersonating a non-admin) always have access.
        if (isAdmin) {
            return true;
        }
        const subscription = normalizeSubscription(state.subscription);

        // If flag doesn't exist, it's enabled by default (unless specifically restricting new features)
        if (!flag) return true;

        if (!flag.isEnabled) return false;

        // If flag.isProOnly is true...
        if (flag.isProOnly) {
            // ...user must be Pro
            if (!subscription.isPro) return false;

            // Check tier level if specified
            if (flag.minTier === 'lifetime' && subscription.tier !== 'lifetime') {
                return false;
            }
        }

        return true;
    },
});
